/**
 * 后台桌面布局（PRD §3.3.1、§8.6、§8.7；platform REQ-8.6-005、REQ-8.7-004、005、009）：
 * - 左侧菜单按 §3.3.1 的 8 个菜单组，只显示本人有 Ann.view 的页面，整组无权限时不显示（菜单按权限过滤）；
 * - 顶栏：当前页面标题；右侧本人姓名、「个人账户」（/self）与「退出登录」（文字按钮）；
 * - 无查看权限的页面显示"你没有这项操作的权限"，不渲染页面；
 * - 后台通用状态（spec-platform F 节）：审计异常只读顶部条（收到 ADMIN_READONLY_MODE 出现；审计恢复后撤下——AA-04 的 readonlyMode、
 *   只读期间每 30 秒探测 A01 顶栏接口的 readonlyMode，或任一写操作成功）；断网"网络已断开，数据更新于 {time}"；
 *   权限变更时当前页面变为无权才提示"权限已变更"并回到工作台；
 * - 锁屏（ADMIN_SESSION_LOCKED、会话 locked 或 ws `admin.session locked`）：员工领域的锁屏遮罩盖在页面上方，页面不卸载、已填数据保留；
 * - ws `admin.session`（spec-core H8、PM5）：权限变更、强制下线、过期时立即重新取会话（不等 60 秒轮询）；
 * - 焦点：键盘可完成全部操作，sticky 顶栏高度写入滚动留白，焦点不被遮挡。
 */
import { useI18n } from '@nexora/i18n/react';
import { Button, EmptyState, ErrorState, GenesisMark, OfflineBanner, Skeleton, cx, isApiError, useToast } from '@nexora/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useMatches, useNavigate } from 'react-router-dom';
import { adminPageById, type AdminPageId } from '../../routes';
import { adminApi } from './api/client';
import { useAdminErrorText } from './api/useAdminErrorText';
import { useAdminTime } from './format';
import {
  READONLY_PROBE_KEY,
  READONLY_PROBE_MS,
  SESSION_QUERY_KEY,
  canView,
  fetchReadonlyMode,
  useStaffSession,
  visibleMenu,
  type StaffSession,
} from './session';
import { LockScreen } from './slots';
import { useAdminChannels, type WsMessage } from '../workbench/realtime';

/** 外壳订阅的后台 ws 频道（本人会话：锁屏、权限变更、强制下线、过期、解锁）。 */
const SESSION_CHANNELS = ['admin.session'] as const;

export interface AdminRouteHandle {
  pageId?: AdminPageId;
  /** 不属于 A01–A57 的页面标题（个人账户） */
  titleKey?: string;
}

export interface AdminShellState {
  staff: StaffSession['staff'];
  perms: ReadonlySet<string>;
  /** 网络是否在线（离线时写按钮置灰） */
  online: boolean;
  /** 审计异常只读模式（写按钮全部置灰） */
  readonly: boolean;
}

const AdminShellContext = createContext<AdminShellState | null>(null);

/** 后台页面读取本人信息、权限与只读 / 离线状态。 */
export function useAdminShell(): AdminShellState {
  const value = useContext(AdminShellContext);
  if (!value) throw new Error('useAdminShell must be used inside <AdminLayout>');
  return value;
}

function useCurrentHandle(): AdminRouteHandle {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const handle = matches[i]?.handle as AdminRouteHandle | undefined;
    if (handle?.pageId || handle?.titleKey) return handle;
  }
  return {};
}

function useOnline(): { online: boolean; since: number | null } {
  const [state, setState] = useState(() => ({ online: typeof navigator === 'undefined' ? true : navigator.onLine, since: null as number | null }));
  useEffect(() => {
    const on = () => setState({ online: true, since: null });
    const off = () => setState({ online: false, since: Date.now() });
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return state;
}

function LayoutSkeleton() {
  return (
    <div aria-busy="true" className="flex min-h-dvh min-w-[1280px] bg-bg">
      <div className="w-64 shrink-0 border-r border-border bg-surface p-4">
        <Skeleton height="1.5rem" className="mb-6" />
        <Skeleton height="1rem" className="mb-3" />
        <Skeleton height="1rem" className="mb-3" />
        <Skeleton height="1rem" />
      </div>
      <div className="flex-1 p-6">
        <Skeleton height="2rem" className="mb-4" />
        <Skeleton height="12rem" />
      </div>
    </div>
  );
}

function DomainIcon({name}:{name:string}){const paths:Record<string,string>={workbench:'M3 3h7v7H3z M14 3h7v7h-7z M3 14h7v7H3z M14 14h7v7h-7z',users:'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M16 3a4 4 0 0 1 0 8 M22 21v-2a4 4 0 0 0-3-3.87 M13 7a4 4 0 1 1-8 0a4 4 0 0 1 8 0',funds:'M20 6c0 2-3.6 4-8 4S4 8 4 6s3.6-4 8-4 8 2 8 4z M4 6v12c0 2 3.6 4 8 4s8-2 8-4V6 M4 12c0 2 3.6 4 8 4s8-2 8-4',machines:'M12 2 3 7v10l9 5 9-5V7z M3 7l9 5 9-5 M12 12v10',growth:'M3 21V3 M3 21h18 M7 16l4-5 4 2 6-9 M17 4h4v4',content:'M4 3h12l4 4v14H4z M16 3v5h4 M8 12h8 M8 16h6',support:'M3 14v-3a9 9 0 0 1 18 0v3 M3 12h4v8H3z M17 12h4v8h-4z M17 20c0 2-2 2-5 2',system:'M12 8a4 4 0 1 0 0 8a4 4 0 0 0 0-8 M12 2v3 M12 19v3 M2 12h3 M19 12h3 M5 5l2 2 M17 17l2 2 M19 5l-2 2 M7 17l-2 2'};return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={paths[name]??paths.workbench}/></svg>;}
function Shell({ session }: { session: StaffSession }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const time = useAdminTime();
  const handle = useCurrentHandle();
  const page = handle.pageId ? adminPageById(handle.pageId) : undefined;
  const perms = useMemo(() => new Set(session.perms), [session.perms]);
  const menu = useMemo(() => visibleMenu(perms), [perms]);
  const activeGroup=menu.find(item=>item.group.modules.includes(page?.module??''));
  const [expandedGroup,setExpandedGroup]=useState(activeGroup?.group.id??'workbench');
  useEffect(()=>{if(activeGroup)setExpandedGroup(activeGroup.group.id);},[activeGroup?.group.id]);
  const { online, since } = useOnline();
  // 进入只读模式的时刻（null = 不在只读模式）；探测结果只认这之后取到的
  const [readonlySince, setReadonlySince] = useState<number | null>(null);
  const readonly = readonlySince !== null;
  const setReadonly = useCallback((on: boolean) => setReadonlySince((since) => (on ? (since ?? Date.now()) : null)), []);
  const [lockedByError, setLockedByError] = useState(false);
  const locked = session.locked || lockedByError;
  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const permVersion = useRef(session.permVersion);

  // sticky 顶栏的实际高度写入滚动留白，键盘焦点不被顶栏遮挡（PRD §8.7）
  useEffect(() => {
    const el = headerRef.current;
    const root = document.documentElement;
    if (!el) return undefined;
    const update = () => root.style.setProperty('--nx-top-bar-h', `${el.getBoundingClientRect().height}px`);
    update();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
    observer?.observe(el);
    return () => observer?.disconnect();
  }, []);

  const title = page ? t(page.titleKey) : handle.titleKey ? t(handle.titleKey) : t('admin.appName');
  useEffect(() => {
    document.title = page || handle.titleKey ? `${title} · ${t('admin.appName')}` : t('admin.appName');
  }, [page, handle.titleKey, title, t]);

  // 只读模式的当前状态（PRD 5.18.6"直到日志恢复"）：AA-04 带回 readonlyMode 时以它为准
  useEffect(() => {
    if (typeof session.readonlyMode === 'boolean') setReadonly(session.readonlyMode);
  }, [session.readonlyMode, setReadonly]);
  // 只读期间写按钮全部置灰、没有写请求能发出：定时探测 A01 顶栏接口，审计恢复即撤下
  const probe = useQuery({
    queryKey: READONLY_PROBE_KEY,
    queryFn: fetchReadonlyMode,
    enabled: readonly && online && canView(perms, 'A01'),
    refetchInterval: READONLY_PROBE_MS,
    gcTime: 0,
  });
  useEffect(() => {
    if (readonlySince !== null && probe.data === false && probe.dataUpdatedAt >= readonlySince) setReadonly(false);
  }, [readonlySince, probe.data, probe.dataUpdatedAt, setReadonly]);

  // 全局错误：锁屏与只读模式；只读期间一次写操作成功也撤下提示
  useEffect(() => {
    const offError = adminApi.onError((error) => {
      if (error.code === 'ADMIN_SESSION_LOCKED') setLockedByError(true);
      if (error.code === 'ADMIN_READONLY_MODE') setReadonly(true);
    });
    const offSuccess = adminApi.onSuccess((context) => {
      if (context.method !== 'GET') setReadonly(false);
    });
    return () => {
      offError();
      offSuccess();
    };
  }, [setReadonly]);

  // ws admin.session（spec-core §1.15、H8、PM5；spec-identity PG-X03、JB-04；D-E2E-ADMIN-01）：锁屏、权限变更、强制下线、过期、
  // 解锁时立即重新取本会话（不等 60 秒轮询）——本会话锁屏则盖上遮罩，权限版本变化走下面的"权限已变更"，会话已结束时 401 回到登录页。
  // 推送按员工发出（同一员工最多 2 个会话），是否锁屏以本会话的 AA-04 为准，不直接盖遮罩
  const onSessionPush = useCallback(
    (msg: WsMessage) => {
      if (msg.type === 'unlocked') setLockedByError(false);
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
    [queryClient],
  );
  useAdminChannels(SESSION_CHANNELS, onSessionPush);

  // 权限变更：已打开的页面变为无权时提示"权限已变更"并回到工作台（PRD 5.17.3"修改岗位"；spec-platform F 节"后台通用"）。
  // 权限版本含全站共用的 A53 覆盖版本，其他岗位的调整也会让版本变化：本页仍有权限时只刷新菜单，不打断当前操作（D-E2E-ADMIN-03）
  const pageId = page?.id;
  useEffect(() => {
    if (permVersion.current === session.permVersion) return;
    permVersion.current = session.permVersion;
    if (!pageId || canView(perms, pageId)) return;
    toast.show({ message: t('admin.common.permChanged') });
    navigate('/', { replace: true });
  }, [session.permVersion, pageId, perms, toast, t, navigate]);

  // 锁屏时页面不可操作（inert），但不卸载
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (locked && LockScreen) el.setAttribute('inert', '');
    else el.removeAttribute('inert');
  }, [locked]);

  const unlocked = useCallback(() => {
    setLockedByError(false);
    void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
  }, [queryClient]);

  const signOut = useCallback(async () => {
    await adminApi.post('/auth/logout', {}).catch(() => undefined);
    queryClient.clear();
    navigate('/login', { replace: true });
  }, [navigate, queryClient]);

  const shell = useMemo<AdminShellState>(() => ({ staff: session.staff, perms, online, readonly }), [session.staff, perms, online, readonly]);
  const allowed = !page || canView(perms, page.id);

  return (
    <AdminShellContext.Provider value={shell}>
      <div ref={contentRef} className="nx-admin-shell flex min-h-dvh min-w-[1280px] bg-bg text-fg" aria-hidden={locked && LockScreen ? true : undefined}>
        <nav
          data-admin-nav=""
          aria-label={t('admin.menu.label')}
          className="nx-admin-nav sticky top-0 flex h-dvh shrink-0 flex-col overflow-y-auto border-r border-border bg-surface px-3 py-4"
        >
          {/* "NEXORA 管理后台"含普通文字，不是纯标识：用正文色（渐变文字对比度不足 4.5:1，PRD §8.7） */}
          <Link to="/" className="px-3 py-3 mb-4" aria-label={t('admin.appName')}><GenesisMark /></Link>
          {menu.map(({ group, pages }) => {
            const headingId = `menu-group-${group.id}`;
            return (
              <section key={group.id} aria-labelledby={headingId} className="mb-1">
                <button type="button" id={headingId} aria-label={t(group.labelKey)} aria-controls={`${headingId}-pages`} aria-expanded={expandedGroup===group.id} className={cx("nx-nav-group",activeGroup?.group.id===group.id&&"is-current")} onClick={()=>setExpandedGroup(value=>value===group.id?'':group.id)}><span className="nx-nav-glyph" aria-hidden="true"><DomainIcon name={group.id}/></span><span>{t(group.labelKey)}</span><span aria-hidden="true" className="ml-auto text-xs">{expandedGroup===group.id?'−':'+'}</span></button>
                <ul id={`${headingId}-pages`} hidden={expandedGroup!==group.id} className="nx-nav-pages flex flex-col gap-0.5">
                  {pages.map((item) => (
                    <li key={item.id}>
                      <NavLink
                        to={item.path}
                        end
                        data-page-link={item.id}
                        className={({ isActive }) =>
                          cx(
                            'flex min-h-touch items-center rounded-lg px-3 py-1.5 text-sm transition-colors duration-micro',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                            isActive ? 'bg-surface-2 font-semibold text-brand-fg' : 'text-fg hover:bg-surface-2',
                          )
                        }
                      >
                        {t(item.titleKey)}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </nav>
        <div className="flex min-w-0 flex-1 flex-col">
          <header ref={headerRef} className="sticky top-0 z-20 border-b border-border bg-bg" data-admin-header="">
            <div className="flex min-h-14 items-center justify-between gap-4 px-6">
              <h1 aria-label={title} className="text-sm font-medium text-fg-muted">{activeGroup&&t(activeGroup.group.labelKey)!==title&&<span className="mr-3">{t(activeGroup.group.labelKey)} <span className="ml-3 opacity-40">/</span></span>}<span className="text-fg">{title}</span></h1>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-fg-muted">{session.staff.name}</span>
                <Link
                  to="/self"
                  className="flex min-h-touch items-center rounded-lg px-3 font-medium text-brand-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                >
                  {t('admin.header.self')}
                </Link>
                <Button variant="text" onClick={() => void signOut()}>
                  {t('admin.header.logout')}
                </Button>
              </div>
            </div>
            {readonly && (
              <p role="alert" className="border-t border-border bg-surface-2 px-6 py-2 text-sm font-medium text-status-danger-fg">
                {t('admin.common.readonly')}
              </p>
            )}
            {!online && <OfflineBanner message={t('admin.common.offline', { time: time(since ?? Date.now()) })} />}
          </header>
          <main id="main" className="nx-admin-main flex-1 px-6 py-5">
            {allowed ? <Outlet /> : <EmptyState title={t('error.ADMIN_PERMISSION_DENIED')} />}
          </main>
        </div>
      </div>
      {locked && LockScreen && <LockScreen staffName={session.staff.name} onUnlocked={unlocked} onSignedOut={() => void signOut()} />}
    </AdminShellContext.Provider>
  );
}

export function AdminLayout() {
  const { t } = useI18n();
  const errorText = useAdminErrorText();
  const session = useStaffSession();
  if (session.data) return <Shell session={session.data} />;
  if (session.isError && !(isApiError(session.error) && session.error.status === 401)) {
    return (
      <div className="flex min-h-dvh min-w-[1280px] items-center justify-center bg-bg text-fg">
        <ErrorState
          title={errorText(session.error)}
          action={
            <Button block onClick={() => void session.refetch()}>
              {t('action.retry')}
            </Button>
          }
        />
      </div>
    );
  }
  return <LayoutSkeleton />;
}
